import { useContext, useMemo, useRef, useState } from 'react'

import { round } from '../../../../app/backtest/utils'
import { clamp } from '../../../../app/shared/clamp'
import { useConst } from '../../../hooks/useConst'
import { useOnMount } from '../../../hooks/useOnMount'
import { parseRouteInitData } from '../../../util-routing'
import { QuoteContext } from '../QuoteContext'
import BarChart, { Bar } from './barChart'

const CHART_MIN_COLS = 5
const CHART_MAX_COLS = 9
const CHART_COLUMN_WIDTH = 40
const CHART_SAFE_SPACE = 80

interface FundamentalChartValue {
  year: number
  value: number
}

interface FAChartProps {
  data: Bar[]
  title: string
  smallRoundPlaces?: number
  largeRoundPlaces?: number
  smallScale?: number
  largeScale?: number
  smallTitleSufix?: string
  largeTitleSufix?: string
}

interface DynamicFAChartProps {
  data: Bar[]
  title: string
  format: (value: number) => string
}

function DynamicFAChart({ data, title, format }: DynamicFAChartProps) {
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const [columns, setColumns] = useState(CHART_MIN_COLS)
  const [ready, setReady] = useState(false)

  const calculateColumns = () => {
    const wrap = wrapRef.current
    if (!wrap) return
    const newColumns = Math.floor((wrap.clientWidth - CHART_SAFE_SPACE) / CHART_COLUMN_WIDTH)
    setColumns(clamp(newColumns, CHART_MIN_COLS, data.length))
    setReady(true)
  }

  const observer = useConst(() => new ResizeObserver(calculateColumns))

  useOnMount(() => {
    const wrap = wrapRef.current
    if (wrap) {
      calculateColumns()
      observer.observe(wrap)
    }
    return () => observer.disconnect()
  })

  const limitedData = useMemo(() => data.slice(0, columns).reverse(), [columns, data])

  return (
    <div className="mx-2.5" ref={wrapRef}>
      {ready && (
        <BarChart
          title={title}
          data={limitedData}
          width={CHART_SAFE_SPACE + CHART_COLUMN_WIDTH * columns}
          height={150}
          marginRight={37}
          valueFormatter={format}
        />
      )}
    </div>
  )
}

function FAChart({
  data,
  title,
  smallRoundPlaces = 2,
  largeRoundPlaces = 0,
  smallScale = 1,
  largeScale = 1,
  smallTitleSufix = '',
  largeTitleSufix = '',
}: FAChartProps) {
  const range = Math.max(...data.map(({ value }) => Math.abs(value ?? 0)))
  const isLarge = Math.abs(range) >= 1000
  const scale = isLarge ? largeScale : smallScale
  const values = data.map((item) => {
    if (!item.value) return item
    const rounded = +round(scale * item.value, isLarge ? largeRoundPlaces : smallRoundPlaces)
    return { ...item, value: rounded }
  })

  return (
    <DynamicFAChart
      title={title + ' ' + (isLarge ? largeTitleSufix : smallTitleSufix)}
      data={values}
      format={(value) => round(value, isLarge ? largeRoundPlaces : smallRoundPlaces)}
    />
  )
}

export default function FundamentalCharts() {
  const { index } = useContext(QuoteContext)

  const routeData = useMemo(() => {
    const initData = parseRouteInitData<{ year: number; values: Array<FundamentalChartValue[]> }>({
      elementId: `fa-init-data-${index}`,
      removeElement: false,
    })

    if (!initData) return null

    return initData.values.map((chartData) => {
      const index = new Map<number, number | null>(chartData.map((item) => [item.year, item.value]))
      const alignedData: Bar[] = []

      // add items from highest year to lowest
      const maxYear = initData.year
      const lowYear = maxYear - CHART_MAX_COLS
      let lastValidIndex = CHART_MIN_COLS

      for (let i = 0; maxYear - i > lowYear; i++) {
        const year = maxYear - i
        const value = index.get(year) ?? null
        if (value != null) {
          lastValidIndex = Math.max(CHART_MIN_COLS, i + 1)
        }
        alignedData.push({ name: `${year}`, value: value })
      }

      // trim empty columns
      return alignedData.slice(0, lastValidIndex)
    })
  }, [index])

  if (!routeData) return null

  return (
    <>
      <FAChart title="EPS" data={routeData[0]} />
      <FAChart
        title="Sales"
        data={routeData[1]}
        largeRoundPlaces={2}
        largeScale={0.001}
        smallTitleSufix="($mln)"
        largeTitleSufix="($bln)"
      />
      <FAChart
        title="Shares Outstanding"
        data={routeData[2]}
        largeRoundPlaces={2}
        largeScale={0.001}
        smallTitleSufix="(mln)"
        largeTitleSufix="(bln)"
      />
    </>
  )
}
